import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Flame, ShoppingBag, Menu, X } from 'lucide-react';
import { NAV } from '../mock/mock';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-fire-ink/95 backdrop-blur-md shadow-[0_8px_30px_-12px_rgba(0,0,0,0.8)]' : 'bg-gradient-to-b from-fire-ink/90 to-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[74px]">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <span className="relative">
              <Flame className="w-7 h-7 text-fire-gold animate-flicker" strokeWidth={1.6} />
            </span>
            <span className="leading-none">
              <span className="block font-marcellus tracking-[0.28em] text-[13px] text-fire-cream">AFTER THE</span>
              <span className="block font-marcellus tracking-[0.28em] text-[15px] text-gradient-gold">FIRE</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-3 py-2 text-[13px] font-body font-medium tracking-wide transition-colors ${
                    active ? 'text-fire-goldlight' : 'text-fire-cream/80 hover:text-fire-goldlight'
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute left-3 right-3 -bottom-0.5 h-px bg-fire-gold transition-transform origin-left duration-300 ${
                      active ? 'scale-x-100' : 'scale-x-0'
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/contact"
              className="hidden lg:inline-flex btn-gold rounded-full px-5 py-2.5 text-[12.5px] font-semibold tracking-wide font-body"
            >
              Contact Dr. Gee
            </Link>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open cart"
              className="relative p-2.5 rounded-full border border-fire-gold/30 text-fire-cream hover:text-fire-goldlight hover:border-fire-gold transition-colors"
            >
              <ShoppingBag className="w-[18px] h-[18px]" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-fire-maroon text-fire-cream text-[10px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
              className="xl:hidden p-2.5 rounded-full border border-fire-gold/30 text-fire-cream"
            >
              {mobileOpen ? <X className="w-[18px] h-[18px]" /> : <Menu className="w-[18px] h-[18px]" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`xl:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out bg-fire-ink/98 backdrop-blur-md border-t border-fire-gold/15 ${
          mobileOpen ? 'max-h-[560px]' : 'max-h-0'
        }`}
      >
        <nav className="px-6 py-4 flex flex-col">
          {NAV.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`py-3 border-b border-fire-gold/10 font-cormorant text-lg ${
                  active ? 'text-fire-goldlight' : 'text-fire-cream/85'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
          <Link to="/contact" className="btn-gold rounded-full text-center mt-4 py-3 font-semibold">
            Contact Dr. Gee
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
